import { useCallback, useEffect, useRef, useState } from 'react';
import type { IMPairingSnapshot } from '../../shared/wechatGateway';
import { i18nService } from '../services/i18n';

/** 扫码状态机按实例/登录代次隔离；写入服务端后只刷新配置，不自动启用机器人。 */
export function useIMPairing(instanceId: string, onPaired: () => Promise<unknown>) {
  const [snapshot, setSnapshot] = useState<IMPairingSnapshot | null>(null);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const generation = useRef(0), startingLock = useRef(false);
  const current = useRef<IMPairingSnapshot | null>(null), timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPoll = useRef<object | null>(null);
  const onPairedRef = useRef(onPaired); onPairedRef.current = onPaired;

  /** 取消旧实例的查询，尽力释放服务器记录，失败由服务端TTL兜底。 */
  const cancel = useCallback(() => {
    generation.current++; pendingPoll.current = null;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const old = current.current; current.current = null;
    if (old) void window.electron.im.cancelPairing?.(instanceId, old.id).catch(() => {});
    startingLock.current = false; setStarting(false); setSnapshot(null); setError('');
  }, [instanceId]);

  useEffect(() => {
    window.addEventListener('auth:identityChanging', cancel);
    return () => { window.removeEventListener('auth:identityChanging', cancel); cancel(); };
  }, [cancel]);

  /** GET轮询串行执行，网络错误保留二维码供重试，绝不重新生成/提交登录。 */
  const poll = async (epoch: number, id: string) => {
    if (generation.current !== epoch || pendingPoll.current) return;
    const operation = {}; pendingPoll.current = operation;
    try {
      const api = window.electron.im.pollPairing;
      if (!api) throw new Error(i18nService.t('imInstancesUnsupported'));
      const result = await api(instanceId, id);
      if (generation.current !== epoch) return;
      current.current = result; setSnapshot(result); setError('');
      if (result.status === 'success') await onPairedRef.current();
      else if (result.status !== 'expired') timer.current = setTimeout(() => void poll(epoch, id), 1500);
    } catch (reason) {
      if (generation.current === epoch) setError(reason instanceof Error ? reason.message : i18nService.t('imPairingFailed'));
    } finally { if (pendingPoll.current === operation) pendingPoll.current = null; }
  };

  /** 同步锁拦截同一事件周期双击；迟到开始结果必须取消，不能覆盖新二维码。 */
  const start = async () => {
    if (startingLock.current) return;
    cancel(); startingLock.current = true; setStarting(true);
    const epoch = generation.current;
    try {
      const api = window.electron.im.startPairing;
      if (!api) throw new Error(i18nService.t('imInstancesUnsupported'));
      const result = await api(instanceId);
      if (generation.current !== epoch) { void window.electron.im.cancelPairing?.(instanceId, result.id).catch(() => {}); return; }
      current.current = result; setSnapshot(result);
      timer.current = setTimeout(() => void poll(epoch, result.id), 1500);
    } catch (reason) {
      if (generation.current === epoch) setError(reason instanceof Error ? reason.message : i18nService.t('imPairingFailed'));
    } finally { if (generation.current === epoch) { startingLock.current = false; setStarting(false); } }
  };
  /** 查询失败时复用原扫码ID，避免重复生成二维码。 */
  const retry = () => {
    if (!current.current || !error) return;
    setError(''); void poll(generation.current, current.current.id);
  };
  return { snapshot, error, starting, start, cancel, retry };
}
